import { useMemo, useState } from "react";
import { formatCohortRange } from "../lib/cohorts";
import { Eye, CheckCircle2, XCircle, HelpCircle, Download, RefreshCw, Mail, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { value: "all", label: "All Orders" },
  { value: "pending_stripe", label: "Pending Card Payment" },
  { value: "pending_bank_transfer", label: "Pending Bank Transfer" },
  { value: "proof_submitted", label: "Proof Submitted (action needed)" },
  { value: "paid", label: "Paid / Enrolled" },
  { value: "needs_clarification", label: "Needs Clarification" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
];

const statusColor = (s) => ({
  paid: "bg-green-100 text-green-800",
  pending_stripe: "bg-yellow-100 text-yellow-800",
  pending_bank_transfer: "bg-yellow-100 text-yellow-800",
  proof_submitted: "bg-blue-100 text-blue-800",
  needs_clarification: "bg-orange-100 text-orange-800",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-slate-200 text-slate-700",
}[s] || "bg-slate-100 text-slate-800");

export default function PaymentVerificationManager({ orders, onRefresh, getAuthHeader }) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("all");
  const [selected, setSelected] = useState(null);
  const [actionDialog, setActionDialog] = useState(null); // {type, order}
  const [actionNotes, setActionNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [bulkDialog, setBulkDialog] = useState(null); // 'verify' | 'cancel' | 'resend'
  const [bulkNotes, setBulkNotes] = useState("");

  const filtered = useMemo(() => {
    let list = filter === "all" ? orders : orders.filter((o) => o.status === filter);
    if (region !== "all") list = list.filter((o) => o.proof_of_payment?.paid_to_region === region);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (o) =>
          (o.customer_name || "").toLowerCase().includes(q) ||
          (o.customer_email || "").toLowerCase().includes(q) ||
          (o.course_title || "").toLowerCase().includes(q) ||
          o.id.toLowerCase().startsWith(q)
      );
    }
    return list;
  }, [orders, filter, search, region]);

  // Keep selection consistent with filtered view
  const filteredIds = useMemo(() => new Set(filtered.map((o) => o.id)), [filtered]);
  const selectedIds = useMemo(
    () => new Set([...checkedIds].filter((id) => filteredIds.has(id))),
    [checkedIds, filteredIds]
  );
  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;

  const toggleAll = () => {
    if (allSelected) setCheckedIds(new Set());
    else setCheckedIds(new Set(filtered.map((o) => o.id)));
  };
  const toggleRow = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const downloadReceipt = async (order) => {
    try {
      const res = await axios.get(`${API}/orders/${order.id}/receipt`, { headers: getAuthHeader(), responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      Object.assign(document.createElement("a"), { href: url, download: `${order.receipt_number || `receipt-${order.id.slice(0, 8)}`}.pdf` }).click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download receipt");
    }
  };

  const downloadProof = async (order) => {
    try {
      const res = await axios.get(`${API}/orders/${order.id}/proof/file`, {
        headers: getAuthHeader(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = order.proof_of_payment?.original_filename || `proof-${order.id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download proof file");
    }
  };

  const submitAction = async () => {
    if (!actionDialog) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/orders/${actionDialog.order.id}/admin-action`,
        { action: actionDialog.type, notes: actionNotes || null },
        { headers: getAuthHeader() }
      );
      toast.success(`Payment ${actionDialog.type.replace(/_/g, " ")} successful`);
      setActionDialog(null);
      setActionNotes("");
      setSelected(null);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const resendInstructions = async (order) => {
    try {
      await axios.post(`${API}/orders/${order.id}/resend-instructions`, {}, { headers: getAuthHeader() });
      toast.success("Bank transfer instructions resent");
    } catch {
      toast.error("Failed to resend instructions");
    }
  };

  const submitBulk = async () => {
    if (!bulkDialog || selectedIds.size === 0) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/orders/bulk-action`,
        { order_ids: [...selectedIds], action: bulkDialog, notes: bulkNotes || null },
        { headers: getAuthHeader() }
      );
      const { success, total } = res.data;
      toast.success(`${success}/${total} orders processed`);
      setBulkDialog(null);
      setBulkNotes("");
      setCheckedIds(new Set());
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-sm">Filter:</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[240px]" data-testid="payment-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={region} onValueChange={setRegion}>
            <SelectTrigger className="w-[240px]" data-testid="bank-region-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All bank accounts</SelectItem>
              <SelectItem value="Jamaica">Paid to Jamaica (JMMB)</SelectItem>
              <SelectItem value="US">Paid to US (Wells Fargo)</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search name, email, course..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-64"
              data-testid="orders-search-input"
            />
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} data-testid="orders-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between bg-brand/5 border border-brand/20 rounded-md px-4 py-3" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { setBulkDialog("resend_instructions"); setBulkNotes(""); }} data-testid="bulk-resend-btn">
              <Mail className="h-4 w-4 mr-2" /> Resend Instructions
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setBulkDialog("cancel"); setBulkNotes(""); }} className="text-red-600 border-red-300" data-testid="bulk-cancel-btn">
              <XCircle className="h-4 w-4 mr-2" /> Cancel
            </Button>
            <Button size="sm" onClick={() => { setBulkDialog("verify"); setBulkNotes(""); }} className="bg-green-600 hover:bg-green-700 text-white" data-testid="bulk-verify-btn">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Verify & Enroll
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCheckedIds(new Set())}>Clear</Button>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground">No orders matching filter</CardContent></Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left py-3 px-3 w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" data-testid="select-all-orders" />
                </th>
                <th className="text-left py-3 px-3 font-medium text-sm">Order</th>
                <th className="text-left py-3 px-3 font-medium text-sm">Customer</th>
                <th className="text-left py-3 px-3 font-medium text-sm">Course</th>
                <th className="text-left py-3 px-3 font-medium text-sm">Method</th>
                <th className="text-left py-3 px-3 font-medium text-sm">Amount</th>
                <th className="text-left py-3 px-3 font-medium text-sm">Status</th>
                <th className="text-left py-3 px-3 font-medium text-sm">Created</th>
                <th className="text-right py-3 px-3 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((order) => (
                <tr key={order.id} className={`border-b hover:bg-slate-50 ${selectedIds.has(order.id) ? "bg-brand/5" : ""}`} data-testid={`pay-row-${order.id}`}>
                  <td className="py-3 px-3">
                    <Checkbox
                      checked={selectedIds.has(order.id)}
                      onCheckedChange={() => toggleRow(order.id)}
                      aria-label={`Select order ${order.id}`}
                      data-testid={`select-order-${order.id}`}
                    />
                  </td>
                  <td className="py-3 px-3 font-mono text-xs">#{order.id.slice(0, 8).toUpperCase()}</td>
                  <td className="py-3 px-3">
                    <p className="font-medium text-sm">{order.customer_name}</p>
                    <p className="text-xs text-muted-foreground">{order.customer_email}</p>
                  </td>
                  <td className="py-3 px-3 text-sm">
                    {order.course_title}
                    {order.cohort && <p className="text-xs text-brand" data-testid={`order-cohort-${order.id}`}>{formatCohortRange(order.cohort)}</p>}
                  </td>
                  <td className="py-3 px-3 text-sm">
                    {order.payment_method === "stripe" ? "Card (Stripe)" : "Bank Transfer"}
                    {order.proof_of_payment?.paid_to_region && (
                      <Badge className={`ml-2 ${order.proof_of_payment.paid_to_region === "US" ? "bg-blue-100 text-blue-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`order-bank-region-${order.id}`}>
                        {order.proof_of_payment.paid_to_region === "US" ? "US" : "Jamaica"}
                      </Badge>
                    )}
                  </td>
                  <td className="py-3 px-3 text-sm font-medium">${order.amount?.toFixed(2)}</td>
                  <td className="py-3 px-3"><Badge className={statusColor(order.status)}>{order.status?.replace(/_/g, " ")}</Badge></td>
                  <td className="py-3 px-3 text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</td>
                  <td className="py-3 px-3 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setSelected(order)} data-testid={`view-order-${order.id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail / Verification Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>Order #{selected.id.slice(0, 8).toUpperCase()}</DialogTitle>
                <DialogDescription>{selected.course_title}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Detail label="Customer" value={selected.customer_name} />
                <Detail label="Email" value={selected.customer_email} />
                <Detail label="Phone" value={selected.customer_phone || "—"} />
                <Detail label="Company" value={selected.company_name || "—"} />
                {selected.cohort && <Detail label="Course Dates" value={`${formatCohortRange(selected.cohort)}${selected.cohort.trainer_name ? ` · ${selected.cohort.trainer_name}` : ""}`} />}
                <Detail label="Payment Method" value={selected.payment_method === "stripe" ? "Credit/Debit Card (Stripe)" : "Bank Transfer"} />
                <Detail label="Amount Due" value={`USD ${Number(selected.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                <Detail label="Status" value={<Badge className={statusColor(selected.status)}>{selected.status?.replace(/_/g, " ")}</Badge>} />
                <Detail label="Created" value={new Date(selected.created_at).toLocaleString()} />
                {selected.stripe_session_id && <Detail label="Stripe Session ID" value={<span className="font-mono text-xs">{selected.stripe_session_id}</span>} />}
              </div>

              {selected.proof_of_payment && (
                <div className="mt-4 border-t pt-4">
                  <h4 className="font-semibold mb-3">Proof of Payment</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Detail label="Submitted" value={selected.proof_of_payment.submitted_at ? new Date(selected.proof_of_payment.submitted_at).toLocaleString() : "—"} />
                    <Detail label="Amount Submitted" value={`USD ${Number(selected.proof_of_payment.amount_transferred || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                    <Detail label="Paid Into" value={selected.proof_of_payment.paid_to_label || "—"} />
                    <Detail label="Sending Bank" value={selected.proof_of_payment.bank_used} />
                    <Detail label="Transaction Ref" value={selected.proof_of_payment.transaction_reference} />
                    <Detail label="Date of Transfer" value={selected.proof_of_payment.date_of_transfer} />
                    {!selected.proof_of_payment.recorded_by_admin && <Detail label="File" value={selected.proof_of_payment.original_filename} />}
                  </div>
                  {selected.proof_of_payment.notes && <p className="mt-3 text-sm bg-slate-50 p-3 rounded"><strong>Notes:</strong> {selected.proof_of_payment.notes}</p>}
                  {!selected.proof_of_payment.recorded_by_admin && (
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => downloadProof(selected)} data-testid="download-proof-btn">
                      <Download className="h-4 w-4 mr-2" /> Download Proof File
                    </Button>
                  )}
                </div>
              )}

              {selected.status === "paid" && (
                <Button variant="outline" size="sm" onClick={() => downloadReceipt(selected)} data-testid="download-receipt-btn">
                  <Download className="h-4 w-4 mr-2" /> Download Receipt{selected.receipt_number ? ` ${selected.receipt_number}` : ""}
                </Button>
              )}

              {selected.admin_notes && (
                <div className="mt-3 bg-orange-50 border border-orange-200 rounded p-3 text-sm">
                  <strong>Admin Notes:</strong> {selected.admin_notes}
                </div>
              )}

              <DialogFooter className="flex-wrap gap-2">
                {selected.payment_method === "bank_transfer" && selected.status !== "paid" && (
                  <Button variant="outline" size="sm" onClick={() => resendInstructions(selected)} data-testid="resend-instr-btn">
                    <Mail className="h-4 w-4 mr-2" /> Resend Bank Instructions
                  </Button>
                )}
                {(selected.status === "proof_submitted" || selected.status === "needs_clarification") && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => { setActionDialog({ type: "request_clarification", order: selected }); setActionNotes(""); }} className="text-orange-600 border-orange-300" data-testid="request-clarification-btn">
                      <HelpCircle className="h-4 w-4 mr-2" /> Request Clarification
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setActionDialog({ type: "reject", order: selected }); setActionNotes(""); }} className="text-red-600 border-red-300" data-testid="reject-btn">
                      <XCircle className="h-4 w-4 mr-2" /> Reject
                    </Button>
                    <Button size="sm" onClick={() => { setActionDialog({ type: "verify", order: selected }); setActionNotes(""); }} className="bg-green-600 hover:bg-green-700 text-white" data-testid="verify-btn">
                      <CheckCircle2 className="h-4 w-4 mr-2" /> Verify Payment
                    </Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Action confirmation dialog */}
      <Dialog open={!!actionDialog} onOpenChange={(o) => !o && setActionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog?.type === "verify" && "Verify Payment & Activate Enrollment"}
              {actionDialog?.type === "reject" && "Reject Payment"}
              {actionDialog?.type === "request_clarification" && "Request Clarification"}
            </DialogTitle>
            <DialogDescription>
              {actionDialog?.type === "verify" && "This will mark the order as paid, activate enrollment, and email the customer."}
              {actionDialog?.type === "reject" && "The customer will be notified and asked to resubmit or contact support."}
              {actionDialog?.type === "request_clarification" && "The customer will be asked to provide more information."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Notes (optional, included in email)</Label>
            <Textarea value={actionNotes} onChange={(e) => setActionNotes(e.target.value)} rows={4} placeholder="Add internal/customer notes..." data-testid="action-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submitAction} disabled={busy} className={actionDialog?.type === "verify" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-brand text-white"} data-testid="confirm-action-btn">
              {busy ? "Processing..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Bulk action confirmation dialog */}
      <Dialog open={!!bulkDialog} onOpenChange={(o) => !o && setBulkDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {bulkDialog === "verify" && `Verify & enroll ${selectedIds.size} orders`}
              {bulkDialog === "cancel" && `Cancel ${selectedIds.size} orders`}
              {bulkDialog === "resend_instructions" && `Resend instructions to ${selectedIds.size} orders`}
            </DialogTitle>
            <DialogDescription>
              {bulkDialog === "verify" && "Marks orders as paid, activates enrollments, and emails confirmations. Only applies to bank-transfer orders awaiting verification."}
              {bulkDialog === "cancel" && "Selected orders will be cancelled. Customers are NOT emailed automatically."}
              {bulkDialog === "resend_instructions" && "Resends bank transfer instructions to each customer by email."}
            </DialogDescription>
          </DialogHeader>
          {bulkDialog !== "resend_instructions" && (
            <div className="space-y-2">
              <Label>Admin notes (optional)</Label>
              <Textarea value={bulkNotes} onChange={(e) => setBulkNotes(e.target.value)} rows={3} data-testid="action-notes" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialog(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submitBulk} disabled={busy}
              className={bulkDialog === "verify" ? "bg-green-600 hover:bg-green-700 text-white" : bulkDialog === "cancel" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-brand text-white"}
              data-testid="confirm-bulk-action">
              {busy ? "Processing..." : `Confirm (${selectedIds.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="font-medium">{value}</p>
  </div>
);
